import { useEffect, useState } from 'react';

export interface ImageNaturalSize {
	width: number;
	height: number;
}

/** 'idle' = no src to load. */
export type ImageNaturalSizeStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface ImageNaturalSizeResult {
	size: ImageNaturalSize | null;
	status: ImageNaturalSizeStatus;
}

// Loads the actual pixel dimensions of whatever image src is currently set on
// the block (post-crop, since cropping replaces the src with the cropped
// raster) — used to placeholder the Width/Height inputs with real numbers and
// to label the Dimension panel with the file's real size.
//
// `status` matters as much as `size`: "haven't loaded yet" and "will never
// load" both give a null size, but only the second should make callers treat
// the dimensions as unobtainable. Conflating them greys the inputs out for the
// duration of every image load.
export function useImageNaturalSize(src?: string | null): ImageNaturalSizeResult {
	const [result, setResult] = useState<ImageNaturalSizeResult>({
		size: null,
		status: 'idle',
	});

	useEffect(() => {
		if (!src) {
			setResult({ size: null, status: 'idle' });
			return;
		}

		// Clear first. A stale size lingering while the new src loads would let
		// callers seed dimensions from the image that was just replaced.
		setResult({ size: null, status: 'loading' });

		let cancelled = false;
		const img = new Image();
		img.onload = () => {
			if (cancelled) return;
			setResult({
				size: { width: img.naturalWidth, height: img.naturalHeight },
				status: 'loaded',
			});
		};
		img.onerror = () => {
			if (cancelled) return;
			setResult({ size: null, status: 'error' });
		};
		img.src = src;

		return () => {
			cancelled = true;
		};
	}, [src]);

	return result;
}
